<script setup="">
import { Trash2Icon, UploadIcon } from "lucide-vue-next";
import { computed } from "vue";
import { ColorPicker } from "vue3-colorpicker";

import SimpleLabel from "@/Components/Form/SimpleLabel.vue";
import HelpText from "@/Components/HelpText.vue";

import { useColor } from "@/composables/color";
import PixabayLoader from "@campaign-js/Components/Partials/PixabayViewer.vue";

const props = defineProps({
  campaign: {
    type: Object,
    required: true
  },
  update: {
    type: Function,
    required: true
  }
});

const color = useColor();

const primaryColor = computed({
  get: () => props.campaign.settings.primary_color,
  set: (value) => props.update("settings.primary_color", value)
});

const textColor = computed({
  get: () => props.campaign.settings.color,
  set: (value) => props.update("settings.color", value)
});

const backgroundColor = computed({
  get: () => props.campaign.settings.background_color,
  set: (value) => props.update("settings.background_color", value)
});

const changeThemeColor = (value) => {
  if (value === "rgba(0, 0, 0, 0)" || value === "transparent" || value === "#ffffff") {
    value = "#ededed";
  }

  backgroundColor.value = value;
  primaryColor.value = color.getComplementaryColor(value);
  textColor.value = color.getAppropriateTextColor(value);
};

const changeBackgroundImage = (image) => {
  props.update("cover_photo_url", image.largeImageURL);
};

const removeCoverPhoto = () => {
  props.update("cover_photo_url", null);
};

const updateCoverPhoto = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const formData = new FormData();
  formData.append("file", file);

  axios
    .post(route("app.ajax.campaigns.upload.cover-photo", [props.campaign.team]), formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    })
    .then(({ data }) => {
      props.update("cover_photo_url", data.url);
    });
};
</script>

<template>
  <div>
    <div class="p-8">
      <SimpleLabel classes="text-600 text-sm">색상 또는 배경 선택</SimpleLabel>

      <div class="mt-4 flex items-center space-x-6">
        <div>
          <SimpleLabel classes="text-600 text-sm">테마 색상</SimpleLabel>
          <div class="flex-center mt-4">
            <ColorPicker v-model:pure-color="backgroundColor" shape="circle" lang="En" @update:pure-color="changeThemeColor($event)" />
          </div>
        </div>
        <div>
          <SimpleLabel classes="text-600 text-sm">강조 색상</SimpleLabel>
          <div class="flex-center mt-4">
            <ColorPicker v-model:pure-color="primaryColor" shape="circle" lang="En" />
          </div>
        </div>
      </div>
    </div>

    <div class="p-8 pt-0">
      <SimpleLabel classes="text-600 text-sm">프로필 배경</SimpleLabel>
      <div class="mt-4">
        <div class="flex justify-between space-x-4">
          <div>
            <label class="flex-center group relative aspect-video h-24 flex-none cursor-pointer overflow-hidden rounded bg-gray-200">
              <input type="file" class="invisible absolute" @change="updateCoverPhoto" />
              <img v-if="campaign.cover_photo_url" :src="campaign.cover_photo_url" alt="background image" class="h-full w-full object-cover" />
              <span v-else class="flex flex-col items-center justify-center gap-y-1">
                <UploadIcon class="h-7 w-7 text-600 group-hover:text-zinc-800 dark:group-hover:text-zinc-200" />
                <span class="text-xs font-bold text-600 group-hover:text-zinc-800 dark:group-hover:text-zinc-200">파일 업로드</span>
                <HelpText>권장 사이즈 1024x500(px)</HelpText>
              </span>

              <button
                v-if="campaign.cover_photo_url"
                class="btn absolute bottom-1 right-1 h-6 w-6 rounded bg-white p-0 transition text-600 hover:scale-110 hover:text-900"
                @click.prevent="removeCoverPhoto"
              >
                <Trash2Icon class="h-3.5 w-3.5" />
              </button>
            </label>
          </div>
          <div></div>
        </div>

        <div class="mt-4 h-[calc(100vh-25rem)]">
          <PixabayLoader @select="changeBackgroundImage" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.vc-color-wrap.round {
  width: 30px !important;
  height: 30px !important;
}
</style>
